import { useCallback } from "react";

// Define types for event handlers and registration records
type AppEventHandler<T = any> = (data: T) => void;

interface RegisteredEventHandler<T = any> {
  originalHandler: AppEventHandler<T>;
  wrappedHandler: (event: Event) => void; // Handles CustomEvent internally
  isExclusive: boolean;
}

// Augment the global Window interface for our event registry
declare global {
  interface Window {
    __appEventRegistry?: Map<string, RegisteredEventHandler<any>[]>;
  }
}

/**
 * Custom hook for emitting and listening to application-wide events.
 * Events are managed via the window object.
 */
export function useApplicationEvents() {
  const getRegistry = useCallback((): Map<
    string,
    RegisteredEventHandler<any>[]
  > => {
    if (!window.__appEventRegistry) {
      // eslint-disable-next-line react-compiler/react-compiler
      window.__appEventRegistry = new Map();
    }
    return window.__appEventRegistry;
  }, []);

  /**
   * Emits an application event.
   * @param eventName The name of the event.
   * @param data The data to pass with the event.
   */
  const emitApplicationEvent = useCallback(
    <TData>(eventName: string, data: TData) => {
      const event = new CustomEvent<TData>(eventName, { detail: data });
      window.dispatchEvent(event);
    },
    []
  );

  /**
   * Registers an event handler for an application event.
   * @param eventName The name of the event.
   * @param callback The function to call when the event is emitted.
   * @param options Optional settings for the event handler.
   * @param options.isExclusive If true, this handler will be the only one for this event.
   *                            Any existing handlers will be removed.
   *                            If an exclusive handler is already set, subsequent non-exclusive
   *                            handlers will not be registered.
   *                            A new exclusive handler will replace an existing exclusive one.
   */
  const registerEventHandler = useCallback(
    <TData>(
      eventName: string,
      callback: AppEventHandler<TData>,
      options: { isExclusive?: boolean } = { isExclusive: true }
    ) => {
      const registry = getRegistry();
      const eventHandlers = registry.get(eventName) || [];
      const isExclusiveRequest = !!options?.isExclusive;

      const wrappedHandler = (event: Event) => {
        // Ensure it's a CustomEvent and has detail property
        if (event instanceof CustomEvent && "detail" in event) {
          callback(event.detail as TData);
        }
      };

      if (isExclusiveRequest) {
        // Remove all existing handlers for this event, as this one wants to be exclusive
        eventHandlers.forEach((reg) => {
          window.removeEventListener(eventName, reg.wrappedHandler);
        });

        const newRegistration: RegisteredEventHandler<TData> = {
          originalHandler: callback,
          wrappedHandler,
          isExclusive: true,
        };
        registry.set(eventName, [newRegistration]);
        window.addEventListener(eventName, wrappedHandler);
      } else {
        // Non-exclusive request
        const existingExclusive = eventHandlers.find((reg) => reg.isExclusive);
        if (existingExclusive) {
          console.warn(
            `Cannot register non-exclusive handler for event "${eventName}" because an exclusive handler is already registered.`
          );
          return;
        }

        // Prevent adding the exact same non-exclusive handler function multiple times
        const alreadyExists = eventHandlers.find(
          (reg) => reg.originalHandler === callback && !reg.isExclusive
        );
        if (alreadyExists) {
          console.warn(
            `Handler function already registered for non-exclusive event "${eventName}".`
          );
          return;
        }

        const newRegistration: RegisteredEventHandler<TData> = {
          originalHandler: callback,
          wrappedHandler,
          isExclusive: false,
        };
        eventHandlers.push(newRegistration);
        registry.set(eventName, eventHandlers);
        window.addEventListener(eventName, wrappedHandler);
      }
    },
    [getRegistry]
  );

  /**
   * Unregisters an event handler.
   * @param eventName The name of the event.
   * @param callback The original callback function that was registered.
   */
  const unregisterEventHandler = useCallback(
    <TData>(eventName: string, callback: AppEventHandler<TData>) => {
      const registry = getRegistry();
      const eventHandlers = registry.get(eventName);

      if (!eventHandlers) {
        return;
      }

      const handlerToRemove = eventHandlers.find(
        (reg) => reg.originalHandler === callback
      );

      if (handlerToRemove) {
        window.removeEventListener(eventName, handlerToRemove.wrappedHandler);
        const updatedHandlers = eventHandlers.filter(
          (reg) => reg.originalHandler !== callback
        );

        if (updatedHandlers.length > 0) {
          registry.set(eventName, updatedHandlers);
        } else {
          registry.delete(eventName);
        }
      }
    },
    [getRegistry]
  );

  return {
    emitApplicationEvent,
    registerEventHandler,
    unregisterEventHandler,
  };
}
